import * as React from 'react';

import { cn } from '@/lib/utils';

export interface SwitchProps extends Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  'onChange'
> {
  checked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
  size?: 'default' | 'sm';
}

const Switch = React.forwardRef<HTMLButtonElement, SwitchProps>(
  (
    { className, checked = false, disabled, onCheckedChange, onClick, size = 'default', ...props },
    ref
  ) => {
    const isSmall = size === 'sm';

    return (
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        data-state={checked ? 'checked' : 'unchecked'}
        disabled={disabled}
        className={cn(
          'peer inline-flex shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50',
          isSmall ? 'h-4 w-7' : 'h-6 w-11',
          checked ? 'bg-primary' : 'bg-muted',
          className
        )}
        onClick={event => {
          onClick?.(event);
          if (!event.defaultPrevented) {
            onCheckedChange?.(!checked);
          }
        }}
        ref={ref}
        {...props}
      >
        <span
          data-state={checked ? 'checked' : 'unchecked'}
          className={cn(
            'pointer-events-none block rounded-full shadow-lg ring-0 transition-transform',
            isSmall ? 'h-3 w-3' : 'h-5 w-5',
            checked ? 'bg-primary-foreground' : 'bg-primary-foreground',
            checked ? (isSmall ? 'translate-x-3' : 'translate-x-5') : 'translate-x-0'
          )}
        />
      </button>
    );
  }
);
Switch.displayName = 'Switch';

export { Switch };
